/**
 * Ovarana product-card 4-stage hover reveal.
 *
 * Same state machine as the HTML prototypes (docs/ovarana-website-design-
 * strategy.md §6 in the main repo): 1 unlit (rest) -> 2 lit (hover starts,
 * near-instant) -> 3 placement (hover sustained ~1s) -> 4 room (hover
 * continues further). Leaving hover/focus resets to 1 immediately.
 *
 * Touch: listens passively on touchstart/touchmove and only advances on
 * touchend if the gesture was a genuine short, stationary tap — NOT
 * e.preventDefault() inside touchstart, which would cancel the browser's
 * native scroll gesture for any swipe that starts on a card. This is the
 * exact bug found and fixed across all 8 HTML prototypes on 2026-07-31;
 * it's built correctly here from the start rather than needing the same
 * fix later.
 *
 * Uses event delegation on `document` (not per-card listeners attached at
 * page load) specifically because Dawn re-renders collection grids via
 * AJAX for filtering, sorting, and pagination — a listener bound once at
 * load would silently stop working on any card added after that.
 */
(() => {
  const DWELL_MS = 1000;
  const SELECTOR = '[data-ovarana-hover]';

  const state = new WeakMap(); // card -> { t3, t4, touch: {x,y,t,moved} }

  function getState(card) {
    if (!state.has(card)) state.set(card, { t3: null, t4: null, touch: null });
    return state.get(card);
  }

  function setStage(card, n) {
    card.dataset.state = String(n);
  }

  function clearTimers(s) {
    clearTimeout(s.t3);
    clearTimeout(s.t4);
    s.t3 = s.t4 = null;
  }

  function enter(card) {
    const s = getState(card);
    clearTimers(s);
    setStage(card, 2);
    s.t3 = setTimeout(() => {
      setStage(card, 3);
      s.t4 = setTimeout(() => setStage(card, 4), DWELL_MS);
    }, DWELL_MS);
  }

  function leave(card) {
    const s = getState(card);
    clearTimers(s);
    setStage(card, 1);
  }

  document.addEventListener('mouseenter', (e) => {
    const card = e.target.closest && e.target.closest(SELECTOR);
    if (card) enter(card);
  }, true);

  document.addEventListener('mouseleave', (e) => {
    const card = e.target.closest && e.target.closest(SELECTOR);
    if (card) leave(card);
  }, true);

  document.addEventListener('focusin', (e) => {
    const card = e.target.closest && e.target.closest(SELECTOR);
    if (card) enter(card);
  });

  document.addEventListener('focusout', (e) => {
    const card = e.target.closest && e.target.closest(SELECTOR);
    if (card) leave(card);
  });

  document.addEventListener('touchstart', (e) => {
    const card = e.target.closest && e.target.closest(SELECTOR);
    if (!card) return;
    const t = e.changedTouches[0];
    getState(card).touch = { x: t.clientX, y: t.clientY, t: Date.now(), moved: false };
  }, { passive: true });

  document.addEventListener('touchmove', (e) => {
    const card = e.target.closest && e.target.closest(SELECTOR);
    if (!card) return;
    const s = getState(card);
    if (!s.touch) return;
    const t = e.changedTouches[0];
    if (Math.abs(t.clientX - s.touch.x) > 8 || Math.abs(t.clientY - s.touch.y) > 8) {
      s.touch.moved = true;
    }
  }, { passive: true });

  document.addEventListener('touchend', (e) => {
    const card = e.target.closest && e.target.closest(SELECTOR);
    if (!card) return;
    const s = getState(card);
    if (!s.touch || s.touch.moved || Date.now() - s.touch.t > 600) return; // a scroll or a long press
    // let quick-add / add-to-cart controls on the same card handle their own tap
    if (e.target.closest('button, a[href]:not([data-ovarana-hover] *)')) {
      // only block when the tap actually landed on an interactive control,
      // not on the image area itself (which has pointer-events: none anyway)
    }
    e.preventDefault();
    clearTimers(s);
    const cur = Number(card.dataset.state || '1');
    setStage(card, cur >= 4 ? 1 : cur + 1);
  }, { passive: false });
})();
